import { keyArray } from "@solid-primitives/keyed"
import { batch, createComputed, createEffect, createMemo, createSignal, untrack, type Accessor } from "solid-js"
import type { Nullable } from "vite-node"
import useGridWidthHeight from "../../../../rx/memo/useGridWidthHeight"
import { useEntityByEntityTypeIdReactiveMap } from "../../../../rx/reactive_map/useEntityByEntityTypeIdReactiveMap"
import { useEntityByLayerIdReactiveMap } from "../../../../rx/reactive_map/useEntityByLayerIdReactiveMap"
import { useEntityByPlayerIdReactiveMap } from "../../../../rx/reactive_map/useEntityByPlayerIdReactiveMap"
import { useEntityByXYIndexReactiveMap } from "../../../../rx/reactive_map/useEntityByXYIndexReactiveMap"
import isIdListSame from "../../../../rx/signal/equals/isIdListSame"
import alwaysNull from "../../../ldsh/alwaysNull"
import appendToMapArray from "../../../ldsh/appendToMapArray"
import syncMapToReactiveMap from "../../../ldsh/copyMapToReactiveMap"
import getID from "../../../ldsh/getID"
import isNotNil from "../../../ldsh/isNotNil"
import { NEUTRAL_PLAYER_COLOR } from "../../../three/PlayerThreeObject/getEntityColor1Material"
import getTerrainMesh1TileWaterElevationV1 from "../../../three/objects/TerrainMesh1V1/getTerrainMesh1TileWaterElevationV1"
import { WaterProximityKind } from "../../Water/WaterProximityKind.enum"
import { ELEVATION_ABSOLUTE_MIN } from "../../entity/EntityElevation/elevation_consts"
import isEntityHasFloorElevation from "../../entity/EntityElevation/isEntityHasFloorElevation"
import isEntityLayerIdTerrain1 from "../../entity/EntityLayerId/isEntityLayerIdTerrain1"
import type { PlayerIdOrNull } from "../../player/PlayerIdOrNull"
import type { Engine } from "../Engine.type"
import type { PlayerGraphNode } from "../PlayerGraphNode/PlayerGraphNode.type"
import getPlayerGraphNode from "../PlayerGraphNode/getPlayerGraphNode"
import type { TileGraphNode } from "../TileGraphNode/TileGraphNode.type"
import createFourCharEdgeCollection from "../TileGraphNode/createFourCharEdgeCollection"
import createTileGraphNodeCollectionMemo from "../TileGraphNode/createTileGraphNodeCollectionMemo"
import type { NeighborWaterElevationCollection } from "../TileGraphNode/createTileGraphNodeWaterElevationCollectionMemo"
import createTileGraphNodeWaterElevationCollectionMemo from "../TileGraphNode/createTileGraphNodeWaterElevationCollectionMemo"
import type { EntityGraphNode } from "./EntityGraphNode.type"
import createEntityGraphNode from "./createEntityGraphNode"

/**
 * Creates a reactive list of entity graph nodes that track entity state and relationships.
 * 
 * @remarks
 * This function creates a reactive system that:
 * 1. Tracks all entities in the game engine
 * 2. Provides reactive access to entity properties and relationships
 * 3. Maintains references to associated tile positions and player data
 * 4. Uses Solid.js reactivity for efficient updates
 * 
 * Key Features:
 * - Efficiently tracks entity positions using a 1D array index (x + y * width)
 * - Maintains reactive links to player data and tile positions
 * - Uses untracked signals for performance optimization
 * - Handles edge cases like missing tile nodes in development
 * 
 * Performance Considerations:
 * - Uses `keyArray` for efficient list updates (only updates changed entities)
 * - Memoizes expensive calculations (tile position lookups, player graphs)
 * - Minimizes reactive dependencies to prevent unnecessary re-renders
 * - Uses untracked signals for stable references
 * 
 * @param engine - The game engine instance containing entity and player state
 * @returns A reactive array of entity graph nodes with the following properties:
 *   - id: The unique entity ID
 *   - etype_id: The entity type ID
 *   - tile: The TileGraphNode this entity is on
 *   - player: The PlayerGraphNode for this entity's player
 */
export default function createEntityGraphNodeList(
  engine: Engine,
) {
  // Memoized collection of tile position nodes for efficient lookups
  const getTileGraphNodeList = createTileGraphNodeCollectionMemo(engine)
  // Reactive grid dimensions for position calculations
  const getGridSize = useGridWidthHeight(engine)

  /**
   * Create a reactive array of entity graph nodes using keyArray for efficient updates.
   * keyArray ensures that only changed entities trigger updates by maintaining stable
   * references based on entity IDs.
   */
  const getter = keyArray(
    // Source function - gets all entities from engine state
    // This is the reactive source that triggers updates
    () => engine.state.ents,
    // Key function - uses entity ID for stable identity
    // This ensures minimal re-renders by tracking entity identity
    getID,
    // Transform function - creates the reactive graph node for each entity
    // This runs once per unique entity and sets up reactive bindings
    (getEntity) => {

      // Helper function to get player ID from entity
      // Used for initial signal setup without creating dependencies
      function getPlayerIdRaw() {
        return getEntity().player_id as PlayerIdOrNull
      }

      // Set up reactive signals for player ID and node index
      // Using untrack to prevent creating dependencies during initialization
      const [getPlayerId, setPlayerId] = createSignal<PlayerIdOrNull>(untrack(getPlayerIdRaw))
      const [getNodeIndex, setNodeIndex] = createSignal<Nullable<number>>(untrack(getNodeIndexRaw))

      const getColor1 = createMemo<number>(() => {
        const playerId = getPlayerId()
        const player = getPlayerGraphNode(engine, playerId)
        return player?.color1() || NEUTRAL_PLAYER_COLOR
      })

      const getColor2 = createMemo<number>(() => {
        const playerId = getPlayerId()
        const player = getPlayerGraphNode(engine, playerId)
        return player?.color2() || NEUTRAL_PLAYER_COLOR
      })

      // Effect to update player ID and node index when entity or grid size changes
      // This ensures our derived state stays in sync with the source entity
      createEffect(() => {
        const { width: gridWidth } = getGridSize()
        const value = getEntity()
        // Update player ID if changed
        setPlayerId(value.player_id as PlayerIdOrNull)
        // Update node index based on entity position
        setNodeIndex(value.x + (value.y * gridWidth))
      })
      // Create the entity graph node by augmenting the base entity with additional properties
      // We use type assertion here because we're extending the base entity with additional properties
      // eslint-disable-next-line solid/reactivity
      const node: EntityGraphNode = createEntityGraphNode(
        getEntity,
        // null because we add them in instantly via computed
        null as unknown as TileGraphNode,
        null as unknown as PlayerGraphNode,
        getColor1,
        getColor2,
        getPlayerId,
      )

      /**
       * Calculates the 1D array index for the entity's position in the grid
       * This is used for efficient lookups in the tile position node list
       */
      function getNodeIndexRaw() {
        const { width: gridWidth } = getGridSize()
        const value = getEntity()
        // Convert 2D grid position to 1D array index: x + (y * width)
        return value.x + (value.y * gridWidth)
      }

      // Update the player reference when player ID changes
      // This ensures the entity always points to the correct player data
      createComputed(() => {
        const playerId = getPlayerId()
        // Get the player graph for this entity's player ID
        // This will be undefined for entities without a player (neutral/global entities)
        node.player = getPlayerGraphNode(engine, playerId)
      })
      // Update the tile reference when position changes
      // This keeps the entity linked to its current tile position
      createComputed(() => {
        const nodeIndex = getNodeIndex()
        // Look up the tile position node using the 1D index
        // Returns null if index is invalid or node doesn't exist
        const tileNodeList = getTileGraphNodeList()
        const tileNode = isNotNil(nodeIndex) ? tileNodeList[nodeIndex] : null

        // In development, throw an error if we can't find the tile position node
        // This helps catch issues with invalid positions or missing tile data
        if (!tileNode) {
          // if (DEV) {
          // console.log('getTileGraphNodeList().length', getTileGraphNodeList().length)
          // console.log('getTileGraphNodeList()', getTileGraphNodeList())
          const entity = getEntity()
          // console.log('entity', deepClone(entity))
          // console.error(new Error(
          //   `Failed to find tile position node for ${entityDisplayName(entity)} ${entity.id} ` +
          //   `at position {x: ${entity.x}, y: ${entity.y}} using index: ${JSON.stringify(nodeIndex)}`
          // ))
          // throw new Error(
          //   `Failed to find tile position node for ${entityDisplayName(entity)} ${entity.id} ` +
          //   `at position {x: ${entity.x}, y: ${entity.y}} using index: ${JSON.stringify(nodeIndex)}`
          // )
          // }

          const getEnts: TileGraphNode["ents"] = (() => [entity]) as unknown as TileGraphNode["ents"]
          if (isEntityLayerIdTerrain1(entity)) {
            ; (getEntity as TileGraphNode['ents']['l1']).id = () => entity.id
              ; (getEntity as TileGraphNode['ents']['l1']).etype_id = () => entity.etype_id
            getEnts.l1 = getEntity as TileGraphNode['ents']['l1']
          } else {
            getEnts.l1 = getTileGrapNodeEntsL1AlwaysNull
          }

          node.tile = {
            index: -1,
            x: entity.x,
            y: entity.y,
            floor_e: () => isEntityHasFloorElevation(entity) ? entity.floor_e : ELEVATION_ABSOLUTE_MIN,
            water_e: () => getTerrainMesh1TileWaterElevationV1(entity),
            ents: getEnts,
            n: null,
            s: null,
            e: null,
            w: null,
            ne: null,
            nw: null,
            se: null,
            sw: null,
            edge: createFourCharEdgeCollection(),
            water_prox: () => WaterProximityKind.None,
            wec: null as unknown as Accessor<NeighborWaterElevationCollection>,
          }
          node.tile.wec = createTileGraphNodeWaterElevationCollectionMemo(engine, node.tile)
          return
        }

        // Update the tile reference
        // Using non-null assertion because we've already checked for null above
        node.tile = tileNode!
      })

      // onMount(() => {
      //   // console.log('EntityGraphNode.onMount')
      // })

      return node
    }
  )

  // prepare reactive maps to be synced
  const playerMap0 = useEntityByPlayerIdReactiveMap(engine)
  const entityTypeMap0 = useEntityByEntityTypeIdReactiveMap(engine)
  const layerMap0 = useEntityByLayerIdReactiveMap(engine)
  const xyIndexMap0 = useEntityByXYIndexReactiveMap(engine)

  // bind the getter to it's reactive parent
  createEffect(() => {
    const { width: gridWidth } = getGridSize()
    const nodes = getter()
    batch(() => {
      // Initialize data structures
      const playerMap1 = new Map()
      const entityTypeMap1 = new Map()
      const layerMap1 = new Map()
      const xyIndexMap1 = new Map()

      for (let i = 0; i < nodes.length; i++) {
        const node = nodes[i]
        const { etype_id, layer_id, tile } = node
        appendToMapArray(entityTypeMap1, etype_id, node)
        appendToMapArray(layerMap1, layer_id, node)

        // Update playerId map
        if ('player_id' in node) {
          appendToMapArray(playerMap1, node.player_id ?? -1, node)
        }

        // Update XY index map
        const { x, y } = tile
        if (x !== undefined && y !== undefined) {
          const xyIndex = x + (y * gridWidth)
          appendToMapArray(xyIndexMap1, xyIndex, node)
        }
      }

      syncMapToReactiveMap(playerMap1, playerMap0, isIdListSame)
      syncMapToReactiveMap(entityTypeMap1, entityTypeMap0, isIdListSame)
      syncMapToReactiveMap(layerMap1, layerMap0, isIdListSame)
      syncMapToReactiveMap(xyIndexMap1, xyIndexMap0, isIdListSame)
    })
  })

  return getter
}

const getTileGrapNodeEntsL1AlwaysNull = (() => null) as unknown as TileGraphNode['ents']['l1']
  ; (getTileGrapNodeEntsL1AlwaysNull as TileGraphNode['ents']['l1']).id = alwaysNull
  ; (getTileGrapNodeEntsL1AlwaysNull as TileGraphNode['ents']['l1']).etype_id = alwaysNull
